import "./App.scss";
import "./assets/reset.css";
import React, { useEffect, useMemo, useState } from "react";
import {
  Route,
  Routes,
  BrowserRouter as Router,
  useLocation,
} from "react-router-dom";
import About from "./pages/About/About";
import Header from "./components/Header";
import Hero from "./pages/Hero/Hero";
import Store from "./pages/Store/Store";
import Footer from "./components/Footer";
import Admin from "./pages/Admin/Admin";
import { useDispatch, useSelector } from "react-redux";
import Loader from "./components/Loader";
import {
  fetchPickupLocations,
  fetchStoreData,
  fetchValidCoupons,
} from "./__redux/slices/StoreSlice";
import { setIsMobile } from "./__redux/slices/AppSlice";
import ThankYou from "./pages/Store/ThankYou";
import Services from "./pages/Services/Services";
import Events from "./pages/Events/Events";
import Donate from "./pages/Donate/Donate";

function App() {
  const [loading, setLoading] = useState(true);
  const selectedData = useSelector((state) => ({
    data: state.storeSlice.googleSheetData,
    status: state.storeSlice.status,
  }));

  const { status } = useMemo(
    () => selectedData,
    [selectedData.data, selectedData.status],
  );

  const isLoading = loading || status === "loading";

  // Keep the loader mounted for the length of its fade-out so it crossfades
  // into the page rather than disappearing instantly.
  const [loaderMounted, setLoaderMounted] = useState(true);
  useEffect(() => {
    if (isLoading) {
      setLoaderMounted(true);
      return;
    }
    const timeout = setTimeout(() => setLoaderMounted(false), 700);
    return () => clearTimeout(timeout);
  }, [isLoading]);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchStoreData());
    dispatch(fetchPickupLocations());
    dispatch(fetchValidCoupons());

    const handleResize = () => {
      dispatch(setIsMobile(window.innerWidth < 1000));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  return (
    <div className="App">
      <Router>
        <AppContent loading={loading} setLoading={setLoading} status={status}>
          {loaderMounted && (
            <Loader hasHeader={true} fadingOut={!isLoading} />
          )}
          <ConditionalHeader />
          <Routes>
            <Route index element={<Hero />} />
            <Route
              path="/store/*"
              element={
                <PageWrapper setLoading={setLoading}>
                  <Store />
                </PageWrapper>
              }
            />
            <Route
              path="/services"
              element={
                <PageWrapper setLoading={setLoading}>
                  <Services />
                </PageWrapper>
              }
            />
            <Route
              path="/events"
              element={
                <PageWrapper setLoading={setLoading}>
                  <Events />
                </PageWrapper>
              }
            />
            <Route
              path="/about"
              element={
                <PageWrapper setLoading={setLoading}>
                  <About />
                </PageWrapper>
              }
            />
            <Route
              path="/admin"
              element={
                <PageWrapper setLoading={setLoading}>
                  <Admin />
                </PageWrapper>
              }
            />
            <Route
              path="/thank-you"
              element={
                <PageWrapper setLoading={setLoading}>
                  <ThankYou />
                </PageWrapper>
              }
            />
            <Route path="/donate" element={<Donate />} />
          </Routes>
          <Footer />
        </AppContent>
      </Router>
    </div>
  );
}

function AppContent({ loading, setLoading, status, children }) {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/" || location.pathname === "") {
      setLoading(false);
    }
  }, [location.pathname, setLoading]);

  return <>{children}</>;
}

function PageWrapper({ setLoading, children }) {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/store")) {
      setLoading(false);
      // Reset scroll to top when navigating to store page
      window.scrollTo(0, 0);
      return;
    }

    setLoading(true);
    const handleAllImagesLoaded = () => {
      setLoading(false);
    };

    const images = document.images;
    const totalImages = images.length;
    let imagesLoaded = 0;

    const imageLoaded = () => {
      imagesLoaded++;
      if (imagesLoaded === totalImages) {
        handleAllImagesLoaded();
      }
    };

    [...images].forEach((img) => {
      if (img.complete) {
        imageLoaded();
      } else {
        img.addEventListener("load", imageLoaded);
        img.addEventListener("error", imageLoaded);
      }
    });

    if (totalImages === 0) {
      handleAllImagesLoaded();
    }

    return () => {
      [...images].forEach((img) => {
        img.removeEventListener("load", imageLoaded);
        img.removeEventListener("error", imageLoaded);
      });
    };
  }, [location.pathname, setLoading]);

  return children;
}

function ConditionalHeader() {
  return <Header />;
}
export default App;
